import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input, RadioGroup, Switch } from "@progress/kendo-react-inputs";
import { Hint } from "@progress/kendo-react-labels";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { plusIcon } from "@progress/kendo-svg-icons";
import { debounce } from "lodash";
import { setWorkflowCustomVariables } from "../../../../actions/taskSidebar";
import { button, icon, label, notifyIcon, number, placeholder, quote } from "../../../../config";
import { popupAlign } from "../../../../helper/common";
import { usePopup } from "../../../../helper/commonHooks";
import { useTeamChipPersistence } from "../../../../shared/components/ChipEditor/TeamChipPersistenceContext";
import { getNotification } from "../../../../utils/common";
import { filterData } from "../../../../utils/kendo";
import ShowIfSection from "../../../Forms/ShowIfSection";
import { useFormInteraction } from "../../../Forms/contexts/FormInteractionContext";
import { deleteWorkflowOrTeamChipVariable } from "../../../TeamSetting/TeamChips/projectVariables.helper";
import { useWorkflowContext } from "../../WorkflowContextProvider";
import { resolveAdminFunctionalAreaType } from "../adminVariableLink.helper";
import { useVariablesContext } from "../context/variableContext";
import { usePayloadBuilder } from "../hooks/usePayloadBuilder";
import { useAdminVariableHelper, useFormChipHelper, useLockTeamChipPresetFields, useUniqueVariableNames } from "../hooks/workflowVariables.hooks";
import "../workflowVariable.scss";
import { CHIP_TYPE_DATA, VARIABLE_ICONS, WORKFLOW_VARIABLE_LABELS } from "../workflowVariables.constants";
import ExpressionEditor from "./ExpressionEditor";
import VariableNameHeader from "./common/VariableNameHeader";

/**
 * used to render workflow variable configuration for dropdown type variables
 * @author {Prachi Jain}
 */
const DropdownConfiguration = ({
  isFormVariable,
  usedWorkflowVariableIds = [],
  onRemoveFromForm,
  formData,
  formId,
  onPanelClose,
  showClickToRemoveFromFormBanner = true,
  handleShowIfChange = () => {},
}) => {
  const [isNameEmpty, setIsNameEmpty] = useState(false);
  const [showOptionError, setShowOptionError] = useState(false);
  const [numericErrorIndex, setNumericErrorIndex] = useState(null);
  const [showExpressionEditor, setShowExpressionEditor] = useState(false);
  const [filteredDropdownOptions, setFilteredDropdownOptions] = useState([]);
  const {
    variableName,
    setVariableName,
    isRequired,
    setIsRequired,
    isDefaultSelection,
    setIsDefaultSelection,
    defaultSelection,
    defaultOptions,
    setDefaultOptions,
    setDefaultSelection,
    isDuplicateName,
    optionsView,
    setOptionsView,
    numberOnly,
    setNumberOnly,
    setHasVarChanges,
    isAutomaticallySetValue,
    setIsAutomaticallySetValue,
    setExpressionValue,
    description,
    showChipName,
    setShowChipName,
    multiSelect,
    setMultiSelect,
    multiDimension,
    setMultiDimension,
    skipDimensionOptionsFetchOnModalOpenRef,
    setShowMultiDimensionChipModal,
  } = useVariablesContext();
  const {
    customWorkflowVariables,
    setCustomWorkflowVariables,
    isNewWorkflowVariable,
    setIsNewWorkflowVariable,
    selectedVariable,
    setSelectedVariable,
    workflow,
    setToggleVariableList,
    workflowDefaults,
  } = useWorkflowContext();
  const { selectedElement } = useFormInteraction();
  const { show, setShow, anchor, contentRef: removeLogRef, blurTimeoutRef, onOpen, onFocus, onBlur } = usePopup();
  const {
    show: showNumberOnlyPopup,
    setShow: setShowNumberOnlyPopup,
    anchor: numberOnlyAnchor,
    contentRef: numberOnlyRef,
    blurTimeoutRef: numberOnlyBlurTimeoutRef,
    onOpen: onNumberOnlyOpen,
    onFocus: onNumberOnlyFocus,
    onBlur: onNumberOnlyBlur,
  } = usePopup();
  const {
    show: showMultiDimensionPopup,
    setShow: setShowMultiDimensionPopup,
    anchor: multiDimensionAnchor,
    contentRef: multiDimensionRef,
    blurTimeoutRef: multiDimensionBlurTimeoutRef,
    onOpen: onMultiDimensionOpen,
    onFocus: onMultiDimensionFocus,
    onBlur: onMultiDimensionBlur,
  } = usePopup();
  const teamChipPersist = useTeamChipPersistence();
  const { saveVariableForExistingWorkflow, saveVariableForNewWorkflow } = useAdminVariableHelper();
  const { checkIsDuplicateName } = useUniqueVariableNames();
  const { buildDropdownPayload } = usePayloadBuilder();
  const { updateFormChipProperty, isFormSpecificVisible } = useFormChipHelper();
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [chipOptionsSearchQuery, setChipOptionsSearchQuery] = useState("");
  const [changeCounter, setChangeCounter] = useState(0);
  const dispatch = useDispatch();

  useEffect(() => {
    setChipOptionsSearchQuery("");
  }, [selectedVariable?.id]);

  // Check if the chip is used in the form (for Form Specific properties)
  const isChipUsedInForm = useMemo(() => {
    if (!isFormVariable) return true; // Not relevant for workflow designer
    return usedWorkflowVariableIds.includes(selectedVariable?.id);
  }, [isFormVariable, usedWorkflowVariableIds, selectedVariable?.id]);

  const lockTeamChipPresetFields = useLockTeamChipPresetFields(isFormVariable, selectedVariable);

  /** Admin chips with options  from company functional areas */
  const resolvedAdminFunctionalAreaType = useMemo(
    () =>
      selectedVariable?.adminType === WORKFLOW_VARIABLE_LABELS.IS_ADMIN_FIELD
        ? resolveAdminFunctionalAreaType(selectedVariable, workflowDefaults?.adminCustomVariables)
        : null,
    [selectedVariable?.adminType, selectedVariable?.adminCustomVariables, selectedVariable?.variableId, workflowDefaults?.adminCustomVariables]
  );

  /** Original indices into defaultOptions for the options editor; LIKE match on label when search is non-empty */
  const visibleOptionIndices = useMemo(() => {
    if (!Array.isArray(defaultOptions) || defaultOptions?.length === 0) return [];
    const normalizedSearchQuery = chipOptionsSearchQuery?.trim()?.toLowerCase() ?? "";
    if (!normalizedSearchQuery) return defaultOptions.map((_, i) => i);
    return defaultOptions.reduce((acc, opt, i) => {
      const labelText = String(opt?.label ?? "");
      if (labelText.toLowerCase().includes(normalizedSearchQuery)) acc.push(i);
      return acc;
    }, []);
  }, [defaultOptions, chipOptionsSearchQuery]);

  // Add initial option if no options exist
  useEffect(() => {
    if ((!defaultOptions || defaultOptions.length === 0) && !selectedVariable?.type) {
      const initialOption = { label: numberOnly ? "1" : "Option 1", value: 1 };
      if (numberOnly) {
        initialOption.number = parseFloat("1");
      }
      setDefaultOptions([initialOption]);
      handleVariableChange();
    }
    const skipPlaceholderOption1 = resolvedAdminFunctionalAreaType != null && resolvedAdminFunctionalAreaType !== "";
    if ((!defaultOptions || defaultOptions.length === 0) && skipPlaceholderOption1) {
      return;
    }
    if (selectedVariable?.adminType === WORKFLOW_VARIABLE_LABELS.IS_ADMIN_FIELD && changeCounter === 0) {
      handleVariableChange();
    }
    const hasEmptyOption = defaultOptions?.some((option) => option === "" || !option.label);
    if (hasEmptyOption && !defaultOptions?.length) {
      setHasVarChanges(false);
    }
    if (defaultOptions?.length > 12 && optionsView) {
      setOptionsView(false);
    }
  }, [defaultOptions, setDefaultOptions, selectedVariable?.attributes?.options, resolvedAdminFunctionalAreaType]);

  useEffect(() => {
    if (selectedVariable?.attributes?.options) {
      setDefaultOptions(selectedVariable?.attributes?.options);
    }
  }, [selectedVariable?.attributes?.options]);

  // Clear numeric error when numberOnly is disabled
  useEffect(() => {
    if (!numberOnly) {
      setNumericErrorIndex(null);
    }
  }, [numberOnly]);

  // Sync filtered dropdown options when defaultOptions change
  useEffect(() => {
    const validOptions = defaultOptions?.filter((option) => option.label && option) || [];
    const sortedOptions = [...validOptions].sort((a, b) => a.label.localeCompare(b.label));
    setFilteredDropdownOptions(sortedOptions);
  }, [defaultOptions]);

  /**
   * Saves dropdown variable configuration using payload builder from context
   * @param {Object} [options]
   * @param {Object} [options.attributeOverrides] - Merged into payload.attributes (e.g. multiDimension before context sync)
   * @author Abhishek Dhillon
   */
  const saveDropdownVariable = async (options = {}) => {
    if (isNameEmpty) {
      getNotification(WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_REQUIRED_ERROR, notifyIcon.WARNING_ICON);
      return;
    }
    if (isDuplicateName) {
      getNotification(WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_DUPLICATE_ERROR, notifyIcon.WARNING_ICON);
      return;
    }
    if (!defaultOptions?.length) {
      getNotification(WORKFLOW_VARIABLE_LABELS.REQUIRED_OPTIONS, notifyIcon.WARNING_ICON);
      return;
    }
    const hasEmptyOption = defaultOptions?.some((option) => option === "" || !option.label);
    if (hasEmptyOption) {
      getNotification(WORKFLOW_VARIABLE_LABELS.EMPTY_OPTION, notifyIcon.WARNING_ICON);
      setShowOptionError(true);
      return;
    }
    // Build payload directly from context using custom hook
    const payload = buildDropdownPayload();
    if (options.attributeOverrides && typeof options.attributeOverrides === "object") {
      payload.attributes = { ...payload.attributes, ...options.attributeOverrides };
    }
    let savedVariable = null;

    if (workflow?.Id) {
      const currentId = payload?.id || selectedVariable?.id;
      if (typeof currentId === "number" && !isNewWorkflowVariable) {
        delete payload.id;
      }
      savedVariable = await saveVariableForExistingWorkflow(payload);
      if (savedVariable && setSelectedVariable) {
        setSelectedVariable(savedVariable);
      }
    } else {
      savedVariable = await saveVariableForNewWorkflow(payload);
      if (teamChipPersist?.projectId && !savedVariable) {
        return null;
      }
      if (!savedVariable) {
        savedVariable = { ...selectedVariable, attributes: { ...selectedVariable?.attributes, ...payload.attributes } };
      }
    }
    setHasVarChanges(false);
    return savedVariable;
  };

  /**
   * Debounced auto-save function using lodash debounce
   * Automatically saves dropdown variable configuration after 2 seconds of inactivity
   * @author Abhishek Dhillon
   */
  const debouncedAutoSave = useCallback(
    debounce(async () => {
      try {
        await saveDropdownVariable();
      } catch (error) {
        console.error("Auto-save failed:", error);
      }
    }, 2000),
    [saveDropdownVariable]
  );

  /**
   * Common function to handle variable changes and trigger auto-save
   * Replaces all setHasVarChanges(true) calls
   * @author Abhishek Dhillon
   */
  const handleVariableChange = useCallback(() => {
    setHasVarChanges(true);
    setChangeCounter((prev) => prev + 1);
    // debouncedAutoSave();
  }, [setHasVarChanges]);

  /**
   * Cleanup debounced function on unmount
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    if (isFormVariable && changeCounter > 0 && !Boolean(selectedVariable?.isProjectVariable)) {
      debouncedAutoSave();
    }
    return () => {
      debouncedAutoSave.cancel();
    };
  }, [changeCounter, isFormVariable]);

  /** Team Chips uses projectVariables delete when no workflow Id */
  const removeVariableHandler = async () => {
    const removedId = selectedVariable?.id;
    const deleteSucceeded = await deleteWorkflowOrTeamChipVariable({
      selectedVariable,
      workflow,
      isNewWorkflowVariable,
      teamChipPersist,
    });
    if (!deleteSucceeded) return;
    setToggleVariableList(false);
    setIsNewWorkflowVariable(false);
    setSelectedVariable(null);
    const filteredVariableList = customWorkflowVariables?.filter((variable) => variable.id !== removedId);
    setCustomWorkflowVariables(filteredVariableList);
    dispatch(setWorkflowCustomVariables(filteredVariableList));
  };

  /**
   * Adds a new dropdown option and tracks changes
   * @author Abhishek Dhillon
   */
  const addDropdownOption = () => {
    if (multiDimension) {
      setShowMultiDimensionChipModal(true);
      return;
    }
    const newOptionValue = getNextOptionValue();
    const newOption = {
      label: numberOnly ? String(newOptionValue) : `Option ${newOptionValue}`,
      value: newOptionValue,
    };
    // Add number key when numberOnly is true
    if (numberOnly) {
      newOption.number = null;
    }
    const newOptions = [...defaultOptions, newOption];
    setDefaultOptions(newOptions);
    // Automatically disable optionsView if more than 12 options
    const validOptionsCount = newOptions.filter((option) => option.label && option.label.trim()).length;
    if (validOptionsCount > 12 && optionsView) {
      setOptionsView(false);
    }
    handleVariableChange();
  };

  /**
   * Gets the next available numeric value for a new option.
   * @author Abhishek Dhillon
   * @returns {number} The next option value (minimum 1)
   */
  const getNextOptionValue = () => {
    const options = Array.isArray(defaultOptions) ? defaultOptions : [];
    const numericValues = options.map((option) => Number(option?.value)).filter((value) => Number.isFinite(value));
    if (numericValues.length === 0) return 1;
    const highestValue = Math.max(1, ...numericValues);
    return Math.max(1, highestValue + 1);
  };

  /**
   * Deletes a dropdown option and tracks changes
   * @author Abhishek Dhillon
   * @param {number} index - Index of the option to delete
   */
  const deleteDropdownOption = (index) => {
    const updatedDefaultOptions = defaultOptions?.filter((_, i) => i !== index);
    const selectedOption = updatedDefaultOptions?.find((option) => option.label === defaultSelection?.label)?.value;
    if (!selectedOption) setDefaultSelection(updatedDefaultOptions[0]);
    if (updatedDefaultOptions?.every((option) => option === "" || !option.label)) {
      setIsDefaultSelection(false);
      setHasVarChanges(false);
    } else {
      handleVariableChange();
    }
    setDefaultOptions([...updatedDefaultOptions]);
    // Clear numeric error if deleting the option with error
    if (numericErrorIndex === index) {
      setNumericErrorIndex(null);
    } else if (numericErrorIndex > index) {
      // Adjust the error index if deleting an option before it
      setNumericErrorIndex(numericErrorIndex - 1);
    }
  };

  /**
   * Handles input change for dropdown options with validation and change tracking
   * @author Abhishek Dhillon
   * @param {number} index - Index of the option being changed
   * @param {string} newLabel - New label value
   */
  const handleInputChange = (index, newLabel) => {
    if (numberOnly && newLabel && !/^\d*\.?\d*$/.test(newLabel)) {
      setNumericErrorIndex(index);
      return; // Don't allow non-numeric characters
    }
    if (numericErrorIndex === index) {
      setNumericErrorIndex(null);
    }

    if (!defaultOptions?.some((option) => option === "" || !option.label)) {
      setShowOptionError(false);
      handleVariableChange();
    }
    const filteredArray = defaultOptions.filter((option) => Number.isFinite(Number(option?.value)));
    const highestValue = filteredArray.length ? Math.max(1, ...filteredArray.map((option) => Number(option?.value))) : 1;
    const updatedDefaultOptions = defaultOptions?.map((item, i) => {
      if (i === index) {
        const updatedOption = { ...item, label: newLabel, value: item.value ? item.value : highestValue + 1 };
        // Add/update number key when numberOnly is true
        if (numberOnly) {
          updatedOption.number = newLabel ? parseFloat(newLabel) : null;
        }
        return updatedOption;
      }
      return item;
    });
    const validOptions = updatedDefaultOptions?.filter((option) => option.label && option);
    if (validOptions?.length) {
      setDefaultSelection(validOptions[0]);
      handleVariableChange();
    }
    if (updatedDefaultOptions?.every((option) => option === "" || !option.label)) {
      setIsDefaultSelection(false);
      setHasVarChanges(false);
    }
    setDefaultOptions(updatedDefaultOptions);
    // Automatically disable optionsView if more than 12 valid options
    const validOptionsCount = updatedDefaultOptions.filter((option) => option.label && option.label.trim()).length;
    if (validOptionsCount > 12 && optionsView) {
      setOptionsView(false);
    }
    handleVariableChange();
  };

  const handleVariableNameChange = (event) => {
    const newVariableName = event.target.value;
    setVariableName(newVariableName);
    checkIsDuplicateName(newVariableName?.trim());
    if (!newVariableName?.trim()) {
      setIsNameEmpty(true);
      setHasVarChanges(false);
      return;
    }
    setIsNameEmpty(false);
    handleVariableChange();
    return;
  };

  /**
   * Handles delete all options and tracks changes
   * @author Abhishek Dhillon
   */
  const handleDeleteAll = () => {
    setDefaultOptions([]);
    setIsDefaultSelection(false);
    setShow(false);
    setHasVarChanges(false);
    setNumericErrorIndex(null);
    setChipOptionsSearchQuery("");
  };

  const hideOnBlur = () => {
    setShow(false);
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === index) return;
    const updatedOptions = [...defaultOptions];
    const [draggedItem] = updatedOptions.splice(draggedIndex, 1);
    const itemToInsert = draggedItem || { value: draggedIndex, label: "" };
    updatedOptions.splice(index, 0, itemToInsert);
    setDefaultOptions(updatedOptions);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setNumericErrorIndex(null);
  };

  /**
   * Handles confirming the number only toggle
   * Filters out non-numeric options and enables numberOnly
   */
  const handleConfirmNumberOnly = () => {
    setNumberOnly(true);
    setShowNumberOnlyPopup(false);
    setHasVarChanges(true);
    if (defaultOptions?.length > 0) {
      const numericOptions = defaultOptions
        .filter((option) => {
          if (!option.label) return true;
          return /^[\d\s]+\.?[\d\s]*$/.test(option.label);
        })
        .map((option) => ({
          ...option,
          number: option.label ? parseFloat(option.label) : null,
        }));

      if (numericOptions.length !== defaultOptions.length) {
        setDefaultOptions(numericOptions);
        if (isDefaultSelection && defaultSelection?.label && !/^[\d\s]+\.?[\d\s]*$/.test(defaultSelection.label)) {
          const firstNumericOption = numericOptions.find((opt) => opt.label);
          if (firstNumericOption) {
            setDefaultSelection(firstNumericOption);
          } else {
            setIsDefaultSelection(false);
          }
        }
      } else {
        // Even if no options were filtered, add number key to all options
        setDefaultOptions(numericOptions);
      }
    }
  };

  /**
   * Handles canceling the number only toggle
   * Closes the popup without making changes
   */
  const handleCancelNumberOnly = () => {
    setShowNumberOnlyPopup(false);
  };

  /**
   * Handles confirming the multi-dimension toggle.
   * Persists multiDimension immediately (autosave); on success updates UI and closes popup.
   */
  const handleConfirmMultiDimension = async () => {
    const saved = await saveDropdownVariable({ attributeOverrides: { multiDimension: true } });
    if (saved) {
      setMultiDimension(true);
      setShowMultiDimensionPopup(false);
      skipDimensionOptionsFetchOnModalOpenRef.current = true;
      setShowMultiDimensionChipModal(true);
    }
  };

  /**
   * Handles canceling the multi-dimension toggle.
   * Closes the popup without enabling multi-dimension.
   */
  const handleCancelMultiDimension = () => {
    setShowMultiDimensionPopup(false);
  };

  /**
   * Handles multi-dimension switch click.
   * When enabling: shows confirmation popup ("This cannot be reverted").
   * When already enabled: toggle is disabled, so no action.
   */
  const handleMultiDimensionChange = () => {
    if (!multiDimension) {
      setShowMultiDimensionPopup(true);
    }
  };

  /**
   * Handles number only switch click
   * Shows confirmation popup when enabling, directly disables when turning off
   */
  const handleNumberOnlyChange = () => {
    if (!numberOnly) {
      setShowNumberOnlyPopup(true);
    } else {
      setNumberOnly(false);
      setHasVarChanges(true);
      setNumericErrorIndex(null);
      // Remove number key from all options when turning off numberOnly
      if (defaultOptions?.length > 0) {
        const updatedOptions = defaultOptions.map((option) => {
          const { number, ...rest } = option;
          return rest;
        });
        setDefaultOptions(updatedOptions);
      }
    }
  };

  /**
   * Handles filter change for the default selection dropdown
   * @param {Object} event - Filter change event
   * @author Abhishek Dhillon
   */
  const handleDropdownFilterChange = (event) => {
    const validOptions = defaultOptions?.filter((option) => option.label && option) || [];
    const filteredList = filterData(event.filter, validOptions, "label");
    const sortedList = [...filteredList].sort((a, b) => a.label.localeCompare(b.label));
    setFilteredDropdownOptions(sortedList);
  };

  return (
    <div className='position-relative'>
      <div className='d-flex flex-column overflow-y-auto overflow-x-hidden dropdown-configuration-section'>
        <VariableNameHeader
          isNameEmpty={isNameEmpty}
          setIsNameEmpty={setIsNameEmpty}
          selectedVariable={selectedVariable}
          removeVariableHandler={removeVariableHandler}
          handleVariableChange={handleVariableChange}
          formData={formData}
          formId={formId}
          onPanelClose={onPanelClose}
          isDeleteDisabled={teamChipPersist?.allowDelete === false}
          isFormVariable={isFormVariable}
          onVariableNameCommitted={!isFormVariable ? saveDropdownVariable : undefined}
        />
        {showClickToRemoveFromFormBanner && isChipUsedInForm && onRemoveFromForm && (
          <div className='form-group mb-2'>
            <div
              className='information-panel w-100 d-flex panel-info cursor-pointer'
              onClick={() => {
                if (onRemoveFromForm && selectedVariable?.id) {
                  onRemoveFromForm(selectedVariable.id);
                  handleVariableChange();
                }
              }}>
              <span className='panel-icon d-flex align-items-center'>{icon.INFO_ICON}</span>
              <div className='panel-body'>
                <div className='panel-message'>{quote.CLICK_TO_REMOVE_FROM_FORM}</div>
              </div>
            </div>
          </div>
        )}

        {/* Info banner when chip is not added to form */}
        {!isChipUsedInForm && (
          <div className='form-group mb-2 cursor-default'>
            <div className='information-panel w-100 d-flex panel-info'>
              <span className='panel-icon d-flex align-items-center'>{icon.INFO_ICON}</span>
              <div className='panel-body'>
                <div className='panel-message'>{quote.UNUSED_CHIP_INFO}</div>
              </div>
            </div>
          </div>
        )}
        <div className='custom-variable-properties pr-2'>
          {/* Automatically Set Value Toggle - Only show in form builder when formId exists */}
          {isFormVariable && isFormSpecificVisible && (
            <>
              <div className='form-section-label mb-2 mt-1 bold '>Form Specific</div>

              {/* Automatically Set Value Toggle */}
              <div className='form-group mb-1'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='automatically-set-value-conf' className='mr-3 font-weight-normal'>
                    {WORKFLOW_VARIABLE_LABELS.AUTOMATICALLY_SET_VALUE}{" "}
                    {isAutomaticallySetValue && isChipUsedInForm && (
                      <span
                        className='cursor-pointer'
                        style={{ color: "#1976d2", textDecoration: "underline" }}
                        onClick={(e) => {
                          e.preventDefault();
                          setShowExpressionEditor(true);
                        }}>
                        ({WORKFLOW_VARIABLE_LABELS.EDIT_EXPRESSION})
                      </span>
                    )}
                  </label>
                  <Switch
                    id='automatically-set-value-conf'
                    className='mb-1 toggle-switch-margin'
                    checked={isAutomaticallySetValue}
                    disabled={!isChipUsedInForm}
                    onChange={() => {
                      const newValue = !isAutomaticallySetValue;
                      setIsAutomaticallySetValue(newValue);
                      updateFormChipProperty("autoSetValue", newValue);
                    }}
                  />
                </div>
              </div>

              {/* Show If - Placeholder for future implementation */}
              <ShowIfSection // Show If Section
                selectedElement={selectedElement}
                handleShowIfChange={handleShowIfChange}
              />
              {/* View as Options */}
              <div className='form-group mb-1'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='view-as-options' className='mr-3 font-weight-normal'>
                    {multiSelect ? "View as Checkboxes" : "View as Option Buttons"}
                  </label>
                  <Switch
                    id='view-as-options'
                    className='mb-1 toggle-switch-margin'
                    checked={optionsView}
                    disabled={
                      !isChipUsedInForm ||
                      isAutomaticallySetValue ||
                      defaultOptions?.filter((option) => option.label && option.label.trim()).length > 12
                    }
                    onChange={() => {
                      const newValue = !optionsView;
                      setOptionsView(newValue);
                      updateFormChipProperty("optionsView", newValue);
                    }}
                  />
                </div>
                {defaultOptions?.filter((option) => option.label && option.label.trim()).length > 12 && (
                  <div className='text-muted small mt-1'>Option buttons view is only available for 12 or fewer options</div>
                )}
              </div>
              <div className='form-group mb-1'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='multi-select' className='mr-3 font-weight-normal'>
                    {WORKFLOW_VARIABLE_LABELS.MULTI_SELECT}
                  </label>
                  <Switch
                    id='multi-select'
                    className='mb-1 toggle-switch-margin'
                    checked={multiSelect}
                    onChange={() => {
                      setMultiSelect(!multiSelect);
                      updateFormChipProperty("multiSelect", !multiSelect);
                    }}
                  />
                </div>
              </div>

              {/* Required field only for smart chip */}
              <div className='form-group mb-1'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='required-field' className='mr-3 font-weight-normal'>
                    {WORKFLOW_VARIABLE_LABELS.REQUIRED}
                  </label>
                  <Switch
                    id='required-field'
                    className='mb-1 toggle-switch-margin'
                    checked={isRequired}
                    disabled={isAutomaticallySetValue}
                    onChange={() => {
                      setIsRequired(!isRequired);
                      updateFormChipProperty("required", !isRequired);
                    }}
                  />
                </div>
              </div>

              {/* Default selection */}
              <div className='form-group mb-0'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='default-selection' className='mr-3 font-weight-normal'>
                    {WORKFLOW_VARIABLE_LABELS.DEFAULT_SELECTION}
                  </label>
                  <Switch
                    id='default-selection'
                    className='mb-1 toggle-switch-margin'
                    disabled={isAutomaticallySetValue || !defaultOptions?.some((option) => option.label)}
                    checked={isDefaultSelection}
                    onChange={(e) => {
                      setIsDefaultSelection(!isDefaultSelection);
                      setDefaultSelection(defaultOptions[0]);
                      updateFormChipProperty("default", e.target.value ? defaultOptions[0] : null);
                    }}
                  />
                </div>
              </div>

              <div className='form-group col-md-12 px-0 '>
                {isDefaultSelection && (
                  <DropDownList
                    id='workflow-variable-dropdown'
                    className='w-100'
                    value={defaultSelection}
                    disabled={isAutomaticallySetValue}
                    onChange={(e) => {
                      setDefaultSelection(e.target.value);
                      updateFormChipProperty("default", e.target.value);
                    }}
                    data={filteredDropdownOptions}
                    textField='label'
                    dataItemKey='value'
                    filterable={true}
                    onFilterChange={handleDropdownFilterChange}
                  />
                )}
              </div>

              {/* Chip Name Configuration */}
              <div className='form-group mb-3'>
                <div className='d-flex justify-content-between mb-2'>
                  <label
                    htmlFor='chip-name-conf'
                    className='mr-2 mb-0 d-flex align-items-center font-weight-normal'
                    style={{ whiteSpace: "nowrap", height: "32px" }}>
                    Chip Name
                  </label>
                  <div className='d-flex align-items-center gap-2'>
                    {/* Toggle Style Buttons */}
                    <div className='d-flex' style={{ borderRadius: "6px", overflow: "hidden", border: "1px solid #d4d4d8" }}>
                      <button
                        type='button'
                        className={`btn ${showChipName ? "btn-primary" : "btn-outline-secondary"}`}
                        disabled={!isChipUsedInForm || isAutomaticallySetValue}
                        onClick={() => {
                          setShowChipName(true);
                          updateFormChipProperty("showChipName", true);
                        }}
                        style={{
                          borderRadius: 0,
                          border: "none",
                          fontSize: "12px",
                          padding: "6px 12px",
                          backgroundColor: showChipName ? "#007bff" : "#ffffff",
                          color: showChipName ? "#ffffff" : "#6c757d",
                          borderRight: "1px solid #d4d4d8",
                        }}>
                        Show
                      </button>
                      <button
                        type='button'
                        className={`btn ${!showChipName ? "btn-primary" : "btn-outline-secondary"}`}
                        disabled={!isChipUsedInForm || isAutomaticallySetValue}
                        onClick={() => {
                          setShowChipName(false);
                          updateFormChipProperty("showChipName", false);
                        }}
                        style={{
                          borderRadius: 0,
                          border: "none",
                          fontSize: "12px",
                          padding: "6px 12px",
                          backgroundColor: !showChipName ? "#007bff" : "#ffffff",
                          color: !showChipName ? "#ffffff" : "#6c757d",
                        }}>
                        Hide
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className='form-section-label mb-2 bold '>General </div>

          {/* Multi-Dimension toggle for dropdown chips (not global/admin presets); once enabled cannot be reverted */}
          {(!isNewWorkflowVariable || isFormVariable) && (
            <div className='form-group mb-1'>
              <div className='d-flex align-items-center justify-content-between'>
                <label htmlFor='multi-dimension' className='mr-3 font-weight-normal'>
                  {WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION}{" "}
                  {multiDimension && (
                    <span
                      className='cursor-pointer'
                      style={{ color: "#1976d2", textDecoration: "underline" }}
                      onClick={(e) => {
                        if (lockTeamChipPresetFields) return;
                        e.preventDefault();
                        setShowMultiDimensionChipModal(true);
                      }}>
                      ({WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_EDIT})
                    </span>
                  )}
                </label>
                <div ref={multiDimensionAnchor}>
                  <Switch
                    id='multi-dimension'
                    className='mb-1 toggle-switch-margin'
                    checked={multiDimension}
                    disabled={lockTeamChipPresetFields || multiDimension}
                    onChange={handleMultiDimensionChange}
                  />
                </div>
              </div>
              <Popup
                show={showMultiDimensionPopup}
                anchor={multiDimensionAnchor.current}
                onOpen={() => onMultiDimensionOpen(multiDimensionRef)}
                className='approval-delete-popup'
                popupAlign={popupAlign("right", "bottom")}
                animate={false}>
                <div className='d-flex flex-column p-2'>
                  <div className='mb-2 text-muted small'>{WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_IRREVERSIBLE}</div>
                  <div className='d-flex justify-content-end align-items-center'>
                    <span className='cursor-pointer red mr-2' onClick={handleCancelMultiDimension}>
                      {icon.CLOSE}
                    </span>
                    <span
                      ref={multiDimensionRef}
                      className='cursor-pointer green'
                      tabIndex={number.ZERO}
                      onFocus={() => onMultiDimensionFocus(multiDimensionBlurTimeoutRef)}
                      onBlur={() => onMultiDimensionBlur(multiDimensionBlurTimeoutRef, handleCancelMultiDimension)}
                      onClick={handleConfirmMultiDimension}>
                      {icon.CHECKBOX}
                    </span>
                  </div>
                </div>
              </Popup>
            </div>
          )}

          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='numbers-only' className='mr-3 font-weight-normal'>
                Numbers Only
              </label>
              <div ref={numberOnlyAnchor}>
                <Switch
                  id='numbers-only'
                  className='mb-1 toggle-switch-margin'
                  checked={numberOnly}
                  disabled={lockTeamChipPresetFields}
                  onChange={handleNumberOnlyChange}
                />
              </div>
            </div>
            <Popup
              show={showNumberOnlyPopup}
              anchor={numberOnlyAnchor.current}
              onOpen={() => onNumberOnlyOpen(numberOnlyRef)}
              className='approval-delete-popup'
              popupAlign={popupAlign("right", "bottom")}
              animate={false}>
              <div className='d-flex flex-column p-2'>
                <div className='mb-2 text-muted small'>Enabling "Number only" removes all non-numeric entries</div>
                <div className='d-flex justify-content-end align-items-center'>
                  <span className='cursor-pointer red mr-2' onClick={handleCancelNumberOnly}>
                    {icon.CLOSE}
                  </span>
                  <span
                    ref={numberOnlyRef}
                    className='cursor-pointer green'
                    tabIndex={number.ZERO}
                    onFocus={() => onNumberOnlyFocus(numberOnlyBlurTimeoutRef)}
                    onBlur={() => onNumberOnlyBlur(numberOnlyBlurTimeoutRef, handleCancelNumberOnly)}
                    onClick={handleConfirmNumberOnly}>
                    {icon.CHECKBOX}
                  </span>
                </div>
              </div>
            </Popup>
          </div>

          <div className='border-top mb-2'></div>
          <div className='row'>
            <div className='col-md-12'>
              <div className='d-flex align-items-center justify-content-between'>
                <div className='form-sub-heading'>{WORKFLOW_VARIABLE_LABELS.OPTIONS}</div>
                <div className='d-flex align-items-center'>
                  <div className='flex-grow-1 px-2' style={{ minWidth: 0 }}>
                    <Input
                      className='form-control form-control-sm options-list-search-input w-100'
                      placeholder={placeholder.SEARCH}
                      value={chipOptionsSearchQuery}
                      autoComplete='off'
                      onChange={(e) => setChipOptionsSearchQuery(e.target.value)}
                    />
                  </div>
                  <div
                    title={WORKFLOW_VARIABLE_LABELS.DELETE_ALL}
                    ref={anchor}
                    onClick={() => {
                      if (!lockTeamChipPresetFields) setShow(!show);
                    }}>
                    <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
                      <Button
                        title={WORKFLOW_VARIABLE_LABELS.DELETE_ALL}
                        disabled={lockTeamChipPresetFields}
                        onClick={() => {
                          if (!lockTeamChipPresetFields) setShow(!show);
                        }}
                        fillMode={"link"}
                        size={"small"}>
                        Delete All
                      </Button>
                    </Tooltip>
                  </div>
                  <Popup
                    show={show}
                    anchor={anchor.current}
                    onOpen={() => onOpen(removeLogRef)}
                    className='approval-delete-popup'
                    popupAlign={popupAlign("right", "bottom")}
                    animate={false}>
                    <span
                      ref={removeLogRef}
                      className='cursor-pointer red'
                      tabIndex={number.ZERO}
                      onFocus={() => onFocus(blurTimeoutRef)}
                      onBlur={() => onBlur(blurTimeoutRef, hideOnBlur)}
                      onClick={handleDeleteAll}>
                      {icon.CHECKBOX}
                    </span>
                  </Popup>
                </div>
              </div>
            </div>
            <div className='col-md-12 option-input chip-options'>
              <div>
                {visibleOptionIndices.length === number.ZERO && chipOptionsSearchQuery.trim() && defaultOptions?.length > number.ZERO && (
                  <div className='text-muted small mt-3'>{quote.NO_OPTIONS_MATCH_YOUR_SEARCH}</div>
                )}
                {visibleOptionIndices.map((i) => {
                  return (
                    <div key={i} className='d-flex align-items-center mt-3 mb-3 chip-options-list'>
                      <span
                        className='drag-icon cursor-move mr-2'
                        draggable={!lockTeamChipPresetFields}
                        onDragStart={() => {
                          if (!lockTeamChipPresetFields) handleDragStart(i);
                        }}
                        onDragOver={(e) => {
                          if (!lockTeamChipPresetFields) handleDragOver(e, i);
                        }}
                        onDragEnd={() => {
                          if (!lockTeamChipPresetFields) handleDragEnd();
                        }}
                        style={lockTeamChipPresetFields ? { opacity: 0.4, cursor: "not-allowed" } : {}}>
                        {icon.DRAG_INDICATOR}
                      </span>
                      <div className='w-100'>
                        <Input
                          className='form-control form-control-sm'
                          placeholder={`Option ${i + 1}`}
                          value={defaultOptions[i]?.label || ""}
                          autoComplete='off'
                          disabled={lockTeamChipPresetFields}
                          onChange={(e) => handleInputChange(i, e.target.value)}
                        />
                        {!defaultOptions[i]?.label && <small className='text-danger'>{showOptionError ? "Please fill values" : ""}</small>}
                        {numberOnly && numericErrorIndex === i && <small className='ml-1 text-danger'>Numeric values only</small>}
                      </div>
                      <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
                        <span
                          className={`ml-2 mr-1 app-icon-medium${lockTeamChipPresetFields ? " disabled-icon" : ""}`}
                          onClick={() => {
                            if (!lockTeamChipPresetFields) deleteDropdownOption(i);
                          }}
                          title={WORKFLOW_VARIABLE_LABELS.DELETE_OPTION}
                          style={lockTeamChipPresetFields ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" } : {}}>
                          {VARIABLE_ICONS.delete}
                        </span>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='update-button-container w-100  text-right d-flex justify-content-end align-items-center'>
        <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
          <div className='d-flex justify-content-end mt-2'>
            <button className='btn add-spam-btn custom-variable' disabled={lockTeamChipPresetFields} onClick={addDropdownOption}>
              {icon.ADD} Add Option
            </button>
            {!isFormVariable && (
              <button
                className='btn btn-primary'
                disabled={isDuplicateName || isNameEmpty || !variableName || lockTeamChipPresetFields}
                onClick={saveDropdownVariable}>
                {isNewWorkflowVariable ? button.SAVE : label.UPDATE}
              </button>
            )}
          </div>
        </Tooltip>
      </div>
      {showExpressionEditor && (
        <ExpressionEditor
          isOpen={showExpressionEditor}
          onClose={() => {
            setShowExpressionEditor(false);
          }}
          onSave={(expression) => {
            setExpressionValue(expression);
            setShowExpressionEditor(false);
          }}
          currentVariableName={variableName}
          expectedVariableType='dropdown'
          selectedVariable={selectedVariable}
          workflow={workflow}
          isNewWorkflowVariable={isNewWorkflowVariable}
          onSaveVariable={saveDropdownVariable}
        />
      )}
    </div>
  );
};

export default DropdownConfiguration;
